import React, { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/ui/lib/utils';

/**
 * Production button (DESIGN.md "Buttons"): 8px radius, 500-weight label,
 * coral primary / ink secondary / outline / naked, 2px ink focus ring offset
 * outside the button on keyboard focus only.
 */
const buttonVariants = cva(
    'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-background disabled:pointer-events-none disabled:border disabled:border-border disabled:bg-surface-subtle disabled:text-ink-disabled',
    {
        variants: {
            variant: {
                primary:
                    'bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-hover',
                secondary:
                    'bg-secondary text-secondary-foreground hover:bg-secondary-hover',
                outline:
                    'border border-border-strong bg-transparent text-ink hover:border-ink hover:bg-surface-subtle',
                naked: 'bg-transparent text-ink hover:bg-surface-subtle',
                destructive: 'bg-destructive text-white hover:bg-ink-strong',
                link: 'h-auto px-0 text-link underline-offset-4 hover:underline',
            },
            size: {
                lg: 'min-h-12 px-6 py-3 text-subtitle-1',
                md: 'min-h-10 px-6 py-2 text-subtitle-1',
                sm: 'min-h-8 px-4 py-1.5 text-subtitle-2',
                icon: 'size-12 rounded-xl bg-surface-subtle text-ink-muted hover:bg-surface-muted',
            },
        },
        defaultVariants: {
            variant: 'primary',
            size: 'md',
        },
    },
);

export interface ButtonProps
    extends
        ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : 'button';
        return (
            <Comp
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                {...props}
            />
        );
    },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
